/**
 * ApproverPicker — dropdown that searches MDT users (by name or email) and
 * adds the picked user to the chain. Excludes the calling user and any user
 * already on the chain. Inline-fetches `/api/users` so the slice surface
 * stays focused on approvals.
 *
 * Mounted by ApprovalChainBuilder via an "Add approver" trigger. Closes on
 * pick or on escape.
 *
 * Linear: MYD-201 (D2).
 */

import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { Check, Loader2, Search, UserPlus } from 'lucide-react'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'

export interface ApproverUser {
	_id: string
	name?: string
	email?: string
	title?: string
	company?: string
	image?: string
}

interface ApproverPickerProps {
	/** User ids already on the chain — filtered out of suggestions. */
	excludeIds: string[]
	onPick: (user: ApproverUser) => void
}

const ApproverPicker = ({ excludeIds, onPick }: ApproverPickerProps) => {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [users, setUsers] = useState<ApproverUser[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const token: string | undefined = useSelector(
		(state: any) => state.userLogin?.userInfo?.token,
	)

	const wrapperRef = useRef<HTMLDivElement | null>(null)

	// Fetch the user list once when the picker first opens. Cached for the
	// lifetime of the mount — chain edits are short-lived enough that staleness
	// isn't worth a refetch on every open.
	useEffect(() => {
		if (!open || users.length > 0 || isLoading) return
		setIsLoading(true)
		axios
			.get('/api/users', { headers: { Authorization: `Bearer ${token}` } })
			.then((res) => {
				setUsers(Array.isArray(res.data) ? res.data : [])
				setError(null)
			})
			.catch((err) => {
				const msg =
					err?.response?.data?.message ||
					err?.message ||
					'Failed to load users'
				setError(msg)
			})
			.finally(() => setIsLoading(false))
	}, [open, token, users.length, isLoading])

	// Close on outside click. Listener only attaches while open so we don't
	// pay the click cost when the picker isn't visible.
	useEffect(() => {
		if (!open) return
		const onClick = (e: MouseEvent) => {
			if (!wrapperRef.current) return
			if (!wrapperRef.current.contains(e.target as Node)) setOpen(false)
		}
		const onEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', onClick)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('mousedown', onClick)
			document.removeEventListener('keydown', onEsc)
		}
	}, [open])

	const exclude = new Set(excludeIds)
	const q = query.trim().toLowerCase()
	const filtered = users
		.filter((u) => !exclude.has(u._id))
		.filter((u) => {
			if (!q) return true
			const name = (u.name || '').toLowerCase()
			const email = (u.email || '').toLowerCase()
			return name.includes(q) || email.includes(q)
		})
		.slice(0, 25)

	const handlePick = (user: ApproverUser) => {
		onPick(user)
		setOpen(false)
		setQuery('')
	}

	return (
		<div ref={wrapperRef} className="relative inline-block">
			<Button
				variant="primary"
				className="gap-1.5"
				onClick={() => setOpen((prev) => !prev)}
			>
				<UserPlus className="w-4 h-4" />
				Add approver
			</Button>

			{open && (
				<div className="absolute z-30 mt-1 w-80 rounded-md border border-gray-200 bg-white shadow-lg">
					<div className="p-2 border-b border-gray-100">
						<div className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-gray-200 focus-within:border-[#0080fe]">
							<Search className="w-3.5 h-3.5 text-gray-400" />
							<input
								autoFocus
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search by name or email…"
								className="flex-1 text-sm outline-none bg-transparent"
							/>
						</div>
					</div>
					<div className="max-h-72 overflow-y-auto py-1">
						{isLoading ? (
							<div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
								<Loader2 className="w-4 h-4 animate-spin" />
								Loading users…
							</div>
						) : error ? (
							<div className="px-3 py-3 text-sm text-red-600">{error}</div>
						) : filtered.length === 0 ? (
							<div className="px-3 py-3 text-sm text-gray-500 italic">
								{q ? 'No users match.' : 'No more users to add.'}
							</div>
						) : (
							filtered.map((u) => (
								<button
									key={u._id}
									type="button"
									onClick={() => handlePick(u)}
									className={cn(
										'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
									)}
								>
									<div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 text-[10px] font-semibold uppercase">
										{(u.name || u.email || '?').slice(0, 1)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="font-medium text-gray-900 truncate">
											{u.name || u.email || 'Unknown'}
										</div>
										{(u.email || u.title) && (
											<div className="text-xs text-gray-500 truncate">
												{u.title ? `${u.title} · ` : ''}
												{u.email}
											</div>
										)}
									</div>
									<Check className="w-3.5 h-3.5 text-gray-300" />
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	)
}

export default ApproverPicker
